import React, { useMemo, useState } from 'react';

import { Alert, CircularProgress, Paper, Stack, Typography } from '@mui/material';

import { useGetServicesCostPrices } from '~/hooks/apis/tours/useTourCostPrices';
import { useGetServicesDeparturesInventory } from '~/hooks/apis/tours/useTourInventory';

import { TourFormDeparture } from '~/services/ToursService';

import { FixedCostPriceItem, ServiceCostPrices, SlidingScalePricingItem, TourService } from '~/types/tours/costPrice';
import { servicePricing } from '~/types/tours/supplier';

import FixedPriceTable from './FixedPriceTable';
import SlidingScaleTable from './SlidingScaleTable';
import TourDepartureSelector from './TourDepartureSelector';
import TourInventorySection from './TourInventorySection';
import TourServiceOptionLabel from './TourServiceOptionLabel';
import { buildScaleAnnotation, fixedPricingAsScale, summariseDepartureCost } from './departureInventory';
import { groupDeparturesForSelector } from './departureRows';
import { formatAmount } from './formatAmount';
import { inventoryAtDate } from './inventorySnapshot';
import useInventorySnapshot from './useInventorySnapshot';

interface Props {
  services: Array<TourService>;
  departures: Array<TourFormDeparture>;
}

function pricesForDeparture(
  data: ServiceCostPrices | undefined,
  departureIds: Set<string>,
  season?: string,
): { sliding: Array<SlidingScalePricingItem>; fixed: Array<FixedCostPriceItem> } {
  const sliding =
    data && 'slidingScale' in data
      ? data.slidingScale.filter((item) => !season || item.season.toLowerCase() === season.toLowerCase())
      : data && 'slidingScaleByDeparture' in data
        ? data.slidingScaleByDeparture.filter((item) => departureIds.has(item.departure))
        : [];
  const fixed =
    data && 'fixed' in data
      ? data.fixed.filter((item) => !season || item.season.toLowerCase() === season.toLowerCase())
      : data && 'fixedByDeparture' in data
        ? data.fixedByDeparture.filter((item) => departureIds.has(item.departure))
        : [];

  return { sliding, fixed };
}

export default function AllServiceCostPrices({ services, departures }: Props) {
  const [selectedDepartureKey, setSelectedDepartureKey] = useState('');
  const results = useGetServicesCostPrices(
    services.map((service) => ({ supplierId: service.supplierId, serviceId: service.id })),
  );
  const departureGroups = useMemo(() => groupDeparturesForSelector(departures), [departures]);
  const visibleDepartureGroups = useMemo(
    () =>
      selectedDepartureKey
        ? departureGroups.filter(([departureKey]) => departureKey === selectedDepartureKey)
        : departureGroups,
    [departureGroups, selectedDepartureKey],
  );
  const selectedDepartures = useMemo(
    () => departureGroups.find(([departureKey]) => departureKey === selectedDepartureKey)?.[1] ?? [],
    [departureGroups, selectedDepartureKey],
  );
  const {
    data: inventory,
    inventoriesByService,
    isLoading: isLoadingInventory,
    error: inventoryError,
  } = useGetServicesDeparturesInventory({
    serviceIds: selectedDepartureKey ? services.map((service) => service.id) : [],
    packageDepartureIds: selectedDepartures.flatMap((departure) => departure.packageDepartureId ?? []),
  });
  const { date: snapshotDate, onDateChange } = useInventorySnapshot(inventory);

  if (results.some((result) => result.isLoading)) {
    return <CircularProgress />;
  }

  const error = results.find((result) => result.error)?.error;
  if (error) {
    return <Alert severity="error">Could not load all service cost prices: {error.message}</Alert>;
  }

  const seasonByDeparture = new Map<string, string>();
  results.forEach((result) => {
    const data = result.data;
    const items =
      data && 'slidingScaleByDeparture' in data
        ? data.slidingScaleByDeparture
        : data && 'fixedByDeparture' in data
          ? data.fixedByDeparture
          : [];
    items.forEach((item) => seasonByDeparture.set(item.departure, item.season));
  });

  return (
    <Stack spacing={3}>
      <TourDepartureSelector
        departures={departures}
        selectedDepartureKey={selectedDepartureKey}
        onSelectDeparture={setSelectedDepartureKey}
      />

      {!!selectedDepartureKey && !!selectedDepartures.length && (
        <Stack spacing={1}>
          <Typography variant="h4" fontWeight={600}>
            Cost summary
          </Typography>
          {isLoadingInventory ? (
            <CircularProgress size={28} />
          ) : (
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              {services.map((service, index) => {
                const departureIds = new Set(selectedDepartures.map((departure) => departure.departureId));
                const season = selectedDepartures
                  .map((departure) => seasonByDeparture.get(departure.departureId))
                  .find(Boolean);
                const { sliding, fixed } = pricesForDeparture(results[index].data, departureIds, season);
                const serviceInventory = inventoriesByService[service.id];
                const snapshot = inventoryAtDate(serviceInventory, snapshotDate);
                const fixedScaleLength =
                  serviceInventory?.entries.filter((entry) => entry.operation === 'allocated').length ?? 1;
                const pricing = sliding.length ? sliding : fixedPricingAsScale(fixed, fixedScaleLength);
                const summary = summariseDepartureCost(snapshot, pricing);

                return (
                  <Paper key={service.id} variant="outlined" sx={{ p: 2, minWidth: 220, flex: '1 1 220px' }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      <TourServiceOptionLabel service={service} />
                    </Typography>
                    <Typography variant="h4" fontWeight={600}>
                      {formatAmount(summary.totalCost)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {summary.currency ?? 'No currency'} · {summary.units} travellers
                    </Typography>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </Stack>
      )}

      {visibleDepartureGroups.map(([groupKey, group]) => {
        const departureIds = new Set(group.map((departure) => departure.departureId));
        const season = group.map((departure) => seasonByDeparture.get(departure.departureId)).find(Boolean);

        return (
          <Stack key={groupKey} spacing={2}>
            <Typography variant="h4" fontWeight={600}>
              {group[0].departureName}
            </Typography>

            {services.map((service, index) => {
              const data = results[index].data;
              const kind = servicePricing(service).kind;
              const { sliding, fixed } = pricesForDeparture(data, departureIds, season);
              const annotation = selectedDepartureKey
                ? buildScaleAnnotation(
                    inventoryAtDate(inventoriesByService[service.id], snapshotDate),
                    group[0].departureName,
                    sliding,
                  )
                : undefined;

              return (
                <Stack key={service.id} spacing={1}>
                  <Typography variant="h6">
                    <TourServiceOptionLabel service={service} />
                  </Typography>
                  {kind === 'SlidingScale' || kind === 'SlidingScaleByDeparture' ? (
                    <SlidingScaleTable pricing={sliding} annotation={annotation} />
                  ) : (
                    <FixedPriceTable prices={fixed} annotation={annotation} />
                  )}
                </Stack>
              );
            })}
          </Stack>
        );
      })}

      {!!selectedDepartureKey && (
        <Stack spacing={1} sx={{ minWidth: 0 }}>
          <Typography variant="h4" fontWeight={600}>
            Inventory
          </Typography>
          <TourInventorySection
            departures={departures}
            selectedDepartureId={selectedDepartureKey}
            inventory={inventory}
            snapshotDate={snapshotDate}
            onDateChange={onDateChange}
            items={[]}
            showCosts={false}
            showCostChart
            isLoading={isLoadingInventory}
            error={inventoryError}
          />
        </Stack>
      )}
    </Stack>
  );
}
